import { FunctionComponent } from "react";
import styled from "styled-components";
import Image from "next/image";

import { colours } from "@/utils";
import { Device } from "@/utils/device";
import Link from "@/components/Link";
import { Course } from "@/types/learning/Course";

type CourseLineupContentProps = {
  courses: Course[];
};

const CourseLineupContent: FunctionComponent<CourseLineupContentProps> = (
  props
) => {
  return (
    <S.Section>
      <S.Title>
        <S.ItalicWrap>Example</S.ItalicWrap> course lineup
      </S.Title>
      <S.CourseList>
        {[...props.courses].slice(0, 8).map((course) => (
          <Link
            href={`/academy/collection/${course.urlSlug}`}
            target="_blank"
            rel="noopener noreferrer"
            key={course.courseName}
          >
            <S.CourseCard>
              <S.ImageContainer>
                <Image
                  src={course.courseCardImage.url}
                  alt={course.courseCardImage.title}
                  layout="fill"
                  objectFit="cover"
                />
              </S.ImageContainer>
              <S.CourseTitle>{course.courseName}</S.CourseTitle>
              <S.CourseInfo>{course.courseDescription}</S.CourseInfo>
            </S.CourseCard>
          </Link>
        ))}
      </S.CourseList>
    </S.Section>
  );
};

const S = () => {};

S.Section = styled.section`
  padding: 5% 10%;
`;

S.Title = styled.h3`
  font-size: 5rem;
  line-height: 5rem;
  letter-spacing: -2px;
  font-family:
    HK Grotesk,
    sans-serif;
  font-weight: 800;
  color: ${colours.charcoal};
  text-align: center;
  padding-bottom: 5%;

  @media (min-width: ${Device.tablet}) {
    font-size: 6rem;
    line-height: 6rem;
  }

  @media (min-width: ${Device.desktop}) {
    font-size: 8rem;
    line-height: 8rem;
  }
`;

S.ItalicWrap = styled.span`
  font-family:
    Roxborough CF,
    sans-serif;
  font-style: italic;
  font-weight: 100;
  display: block;
`;

S.CourseList = styled.ul`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-areas:
    ". ."
    ". ."
    ". ."
    ". .";
  gap: 10px;

  @media (min-width: ${Device.mobile}) {
    gap: 20px;
  }

  @media (min-width: ${Device.tablet}) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-template-areas:
      ". . . ."
      ". . . .";
  }

  @media (min-width: ${Device.desktop}) {
    max-width: 1440px;
    gap: 30px;
  }
`;

S.CourseCard = styled.li`
  list-style-type: none;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: scale(1.02);
  }
`;

S.ImageContainer = styled.div`
  position: relative;
  aspect-ratio: 1;
  width: 100%;
`;

S.CourseTitle = styled.h5`
  font-size: 1.6rem;
  line-height: 2.5rem;
  padding: 1rem 0;

  @media (min-width: ${Device.desktop}) {
    font-size: 2rem;
    padding: 2rem 0;
  }
`;

S.CourseInfo = styled.p`
  font-size: 1.6rem;

  @media (min-width: ${Device.desktop}) {
    font-size: 1.8rem;
  }
`;

export default CourseLineupContent;
